import { useState } from "react";
import { DbNewsItem } from "../../../lib/supabase";
import { LANGUAGES } from "../../../lib/newsUtils";

// ─── Types ────────────────────────────────────────────────────────────────────
type SortKey = "title" | "category" | "status" | "date" | "deadline";
type SortDir = "asc" | "desc";

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORY_COLORS: Record<string, string> = {
  "grant-call": "bg-gold/10 text-gold-dark",
  news: "bg-navy/10 text-navy",
  event: "bg-emerald-50 text-emerald-700",
  result: "bg-cool-gray-100 text-navy/60",
};

// ─── Sorting ──────────────────────────────────────────────────────────────────
function sortItems(items: DbNewsItem[], key: SortKey, dir: SortDir): DbNewsItem[] {
  return [...items].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case "title":
        cmp = a.title.localeCompare(b.title);
        break;
      case "category":
        cmp = a.category_label.localeCompare(b.category_label);
        break;
      case "status":
        // published first when asc
        cmp = a.status === b.status ? 0 : a.status === "published" ? -1 : 1;
        break;
      case "date":
        cmp = new Date(a.date).getTime() - new Date(b.date).getTime();
        break;
      case "deadline":
        if (!a.deadline && !b.deadline) cmp = 0;
        else if (!a.deadline) cmp = 1;
        else if (!b.deadline) cmp = -1;
        else cmp = new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        break;
    }
    return dir === "asc" ? cmp : -cmp;
  });
}

// ─── Sort Icon ────────────────────────────────────────────────────────────────
function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) {
    return (
      <span className="inline-flex flex-col h-3 justify-center opacity-25 ml-1">
        <i className="ri-arrow-up-s-line text-[10px] leading-none" />
        <i className="ri-arrow-down-s-line text-[10px] leading-none" />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center ml-1 text-navy">
      <i className={`${dir === "asc" ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm leading-none`} />
    </span>
  );
}

// ─── Sortable Header Cell ─────────────────────────────────────────────────────
interface SortableThProps {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onClick: (key: SortKey) => void;
  className?: string;
  align?: "left" | "right";
}

function SortableTh({ label, sortKey, current, dir, onClick, className = "", align = "left" }: SortableThProps) {
  const isActive = current === sortKey;
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider select-none cursor-pointer group transition-colors ${
        isActive ? "text-navy" : "text-navy/40 hover:text-navy/70"
      } text-${align} ${className}`}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        <SortIcon active={isActive} dir={dir} />
      </span>
    </th>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────
interface NewsTableProps {
  items: DbNewsItem[];
  translationMap: Record<string, string[]>;
  aiTranslationMap: Record<string, string[]>;
  onEdit: (item: DbNewsItem) => void;
  onDelete: (item: DbNewsItem) => void;
  onToggleStatus: (item: DbNewsItem) => void;
}

// ─── Language badges ──────────────────────────────────────────────────────────
function LangBadges({ itemId, translationMap, aiTranslationMap }: { itemId: string; translationMap: Record<string, string[]>; aiTranslationMap: Record<string, string[]> }) {
  return (
    <div className="flex items-center gap-1 mt-1.5">
      {LANGUAGES.map((lang) => {
        const isDefault = lang.code === "en";
        const hasTranslation = isDefault || (translationMap[itemId] ?? []).includes(lang.code);
        const isAITranslated = !isDefault && (aiTranslationMap[itemId] ?? []).includes(lang.code);
        return (
          <span
            key={lang.code}
            title={
              isDefault
                ? "English — default language"
                : isAITranslated
                ? `${lang.label} — AI translated`
                : hasTranslation
                ? `${lang.label} — manual translation`
                : `${lang.label} — no translation yet`
            }
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide leading-none transition-colors ${
              isDefault
                ? "bg-navy/10 text-navy border border-navy/15"
                : isAITranslated
                ? "bg-violet-50 text-violet-700 border border-violet-200"
                : hasTranslation
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-transparent text-navy/20 border border-dashed border-cool-gray-200"
            }`}
          >
            {isAITranslated && (
              <i className="ri-sparkling-2-line text-[8px]" />
            )}
            {!isAITranslated && hasTranslation && !isDefault && (
              <i className="ri-check-line text-[8px]" />
            )}
            {!hasTranslation && !isDefault && (
              <i className="ri-add-line text-[8px]" />
            )}
            {lang.code.toUpperCase()}
          </span>
        );
      })}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function NewsTable({ items, translationMap, aiTranslationMap, onEdit, onDelete, onToggleStatus }: NewsTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = sortItems(items, sortKey, sortDir);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  if (items.length === 0) {
    return (
      <div className="text-center py-20 text-navy/40">
        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
          <i className="ri-newspaper-line text-4xl" />
        </div>
        <p className="text-sm font-medium">No articles yet</p>
        <p className="text-xs mt-1">Click &quot;Add Article&quot; to publish your first post.</p>
      </div>
    );
  }

  const sortThProps = { current: sortKey, dir: sortDir, onClick: handleSort };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px]">
        <thead>
          <tr className="border-b border-cool-gray-200 bg-cool-gray-50/60">
            {/* Article — sortable by title */}
            <SortableTh label="Article" sortKey="title" {...sortThProps} className="rounded-tl-none" />

            {/* Category — sortable */}
            <SortableTh label="Category" sortKey="category" {...sortThProps} />

            {/* Status — sortable */}
            <SortableTh label="Status" sortKey="status" {...sortThProps} />

            {/* Date — sortable */}
            <SortableTh label="Published" sortKey="date" {...sortThProps} />

            {/* Deadline — sortable */}
            <SortableTh label="Deadline" sortKey="deadline" {...sortThProps} />

            {/* Actions — not sortable */}
            <th className="px-4 py-3 text-xs font-semibold text-navy/40 uppercase tracking-wider text-right">
              Actions
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-cool-gray-100">
          {sorted.map((item) => (
            <tr
              key={item.id}
              className={`hover:bg-cool-gray-50 transition-colors group ${
                item.status === "draft" ? "opacity-60" : ""
              }`}
            >
              {/* Article */}
              <td className="px-4 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex-shrink-0 rounded-md overflow-hidden border border-cool-gray-200">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full bg-cool-gray-100 flex items-center justify-center">
                        <i className="ri-image-line text-navy/20 text-lg" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-navy leading-snug line-clamp-2 max-w-xs">
                      {item.title}
                    </p>
                    <p className="text-xs text-navy/35 mt-0.5 font-mono">{item.slug}</p>
                    <LangBadges itemId={item.id} translationMap={translationMap} aiTranslationMap={aiTranslationMap} />
                  </div>
                </div>
              </td>

              {/* Category */}
              <td className="px-4 py-4">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CATEGORY_COLORS[item.category]}`}>
                  {item.category_label}
                </span>
              </td>

              {/* Status */}
              <td className="px-4 py-4">
                <button
                  onClick={() => onToggleStatus(item)}
                  title={item.status === "published" ? "Click to set as Draft" : "Click to Publish"}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    item.status === "published"
                      ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      : "bg-amber-50 text-amber-700 hover:bg-amber-100"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      item.status === "published" ? "bg-emerald-500" : "bg-amber-400"
                    }`}
                  />
                  {item.status === "published" ? "Published" : "Draft"}
                </button>
              </td>

              {/* Published date */}
              <td className="px-4 py-4">
                <span className="text-sm text-navy/60">{formatDate(item.date)}</span>
              </td>

              {/* Deadline */}
              <td className="px-4 py-4">
                {item.deadline ? (
                  <span className="text-xs text-red-500 font-medium flex items-center gap-1">
                    <i className="ri-time-line" />
                    {formatDate(item.deadline)}
                  </span>
                ) : (
                  <span className="text-xs text-navy/25">—</span>
                )}
              </td>

              {/* Actions */}
              <td className="px-4 py-4">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onEdit(item)}
                    className="w-8 h-8 flex items-center justify-center text-navy/40 hover:text-navy hover:bg-cool-gray-100 rounded-md transition-colors cursor-pointer"
                    title="Edit article"
                  >
                    <i className="ri-pencil-line text-base" />
                  </button>
                  <button
                    onClick={() => onDelete(item)}
                    className="w-8 h-8 flex items-center justify-center text-navy/40 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                    title="Delete article"
                  >
                    <i className="ri-delete-bin-line text-base" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-cool-gray-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 flex-wrap">
          <p className="text-xs text-navy/35">
            {sorted.length} article{sorted.length !== 1 ? "s" : ""}
            {" · "}sorted by{" "}
            <span className="font-medium text-navy/50">
              {sortKey === "date" ? "publication date" : sortKey}
            </span>{" "}
            {sortDir === "asc" ? "↑" : "↓"}
          </p>
          {/* Translation stats */}
          {LANGUAGES.filter((l) => l.code !== "en").map((lang) => {
            const translatedCount = sorted.filter(
              (item) => (translationMap[item.id] ?? []).includes(lang.code)
            ).length;
            const aiCount = sorted.filter(
              (item) => (aiTranslationMap[item.id] ?? []).includes(lang.code)
            ).length;
            return (
              <span key={lang.code} className="flex items-center gap-2 text-xs">
                <span className="flex items-center gap-1 text-navy/35">
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full ${
                      translatedCount === sorted.length
                        ? "bg-emerald-400"
                        : translatedCount > 0
                        ? "bg-amber-400"
                        : "bg-cool-gray-300"
                    }`}
                  />
                  {lang.label.toUpperCase()}:{" "}
                  <span className="font-medium text-navy/50">
                    {translatedCount}/{sorted.length}
                  </span>
                </span>
                {aiCount > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-violet-500/70">
                    <i className="ri-sparkling-2-line text-[10px]" />
                    <span className="font-medium">{aiCount} AI</span>
                  </span>
                )}
              </span>
            );
          })}
        </div>
        <button
          onClick={() => { setSortKey("date"); setSortDir("desc"); }}
          className="text-xs text-navy/35 hover:text-navy/60 transition-colors cursor-pointer whitespace-nowrap"
        >
          Reset sort
        </button>
      </div>
    </div>
  );
}
